import React, { ChangeEvent, FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { grammarPoints } from "../../consts/grammarPoints";
import { lessonCultureNotes } from "../../consts/lessonCultureNotes";
import { allKanjiMeanings } from "../../consts/allKanjiMeanings";
import { fetchVocabularyLevel, wordMatchesRomaji } from "../../utils/vocabularyData";
import { useLocalizedPath } from "../../i18n/localizedPath";
import { useSettledValue } from "../../hooks/useSettledValue";
import { forms, isSearchable, normalize, SEARCH_IDLE_DELAY_MS } from "./searchQuery";
import styles from "./globalSearch.module.scss";

type SearchResult = { id: string; title: string; type: string; excerpt: string; to: string; terms: string[] };
type Loaded = { query: string; language: string; vocabulary: SearchResult[]; lessons: SearchResult[]; blogs: SearchResult[] };
const NOTHING_LOADED: Loaded = { query: "", language: "", vocabulary: [], lessons: [], blogs: [] };
type BlogPostRecord = { slug: string; lang?: string; ptSlug?: string; title: string; excerpt: string; keywords?: string[]; content: unknown };

const matches = (result: SearchResult, query: string) => {
    const queryForms = forms(query);
    return queryForms.some((needle) => result.terms.some((term) => forms(term).some((form) => form.includes(needle))));
};

function stringsFrom(value: unknown): string[] {
    if (typeof value === "string") return [value];
    if (Array.isArray(value)) return value.flatMap(stringsFrom);
    if (value && typeof value === "object" && "label" in value) return stringsFrom((value as { label?: unknown }).label);
    return [];
}

function textFromArticle(content: unknown): string[] {
    if (!Array.isArray(content)) return [];
    return content.flatMap((block) => {
        if (!block || typeof block !== "object") return [];
        const fields = block as Record<string, unknown>;
        const table = fields.table && typeof fields.table === "object" ? fields.table as Record<string, unknown> : undefined;
        return ["text", "heading", "list", "parts"].flatMap((key) => stringsFrom(fields[key]))
            .concat(table ? ["caption", "headers", "rows"].flatMap((key) => stringsFrom(table[key])) : []);
    });
}

// The blog articles are several MB, so they are loaded with the vocabulary and lessons when a search runs, never with the main bundle.
function blogResults(posts: BlogPostRecord[], portugueseBlogContent: Record<string, unknown>, language: string, i18n: any): SearchResult[] {
    const prefix = language === "pt" ? "/pt" : "";
    return posts
        .filter((post) => language !== "pt" || !post.ptSlug)
        .map((post) => {
            const translated = language === "pt" && post.lang !== "pt-PT" ? portugueseBlogContent[post.slug] : undefined;
            const content = translated || post.content;
            const title = language === "pt" && translated
                ? i18n.getResource("pt", "blogPage", `posts.${post.slug}.title`) || post.title
                : post.title;
            const excerpt = language === "pt" && translated
                ? i18n.getResource("pt", "blogPage", `posts.${post.slug}.excerpt`) || post.excerpt
                : post.excerpt;
            const to = language === "pt" && translated ? `/pt/blog/${post.slug}` : `${prefix}/blog/${post.slug}`;
            return { id: `blog-${post.slug}`, title, type: language === "pt" ? "Artigo" : "Blog article", excerpt,
                to, terms: [title, excerpt, ...(post.keywords || []), ...textFromArticle(content)] };
        });
}

// Everything that is searched without loading anything: `before` ranks ahead of the blog articles, `after` behind them.
function contentIndex(language: string): { before: SearchResult[]; after: SearchResult[] } {
    const prefix = language === "pt" ? "/pt" : "";
    const culture = Object.entries(lessonCultureNotes).flatMap(([lesson, notes]) => notes.map((note) => ({
        id: `culture-${lesson}-${note.title}`, title: `${lesson}: ${note.title}`, type: language === "pt" ? "Nota cultural" : "Cultural note",
        excerpt: note.body, to: `${prefix}/lessons?lesson=${lesson.replace("Lesson ", "")}`,
        terms: [lesson, note.title, note.body, note.searchTerms],
    })));
    const grammar = grammarPoints.map((point) => ({
        id: `grammar-${point.id}`, title: point.title || point.pattern, type: language === "pt" ? "Gramática" : "Grammar",
        excerpt: point.meaning, to: `${prefix}/grammar/${point.id}`,
        terms: [
            point.pattern,
            point.title,
            point.meaning,
            point.explanation,
            point.formation,
            ...((point as { keywords?: string[] }).keywords ?? []),
            ...point.examples.flatMap((example) => [example.japanese, example.reading, example.english]),
        ],
    }));
    const kanji = allKanjiMeanings.map((item) => ({
        id: `kanji-${item.symbol}`, title: item.symbol, type: language === "pt" ? "Kanji" : "Kanji", excerpt: item.meaning,
        to: `${prefix}/kanji/${item.symbol}`, terms: [item.symbol, item.meaning],
    }));
    const pages: SearchResult[] = [
        ["Vocabulary", "Vocabulary", "Search Japanese words by meaning, reading or romaji.", "/vocabulary"],
        ["Grammar", "Grammar", "Japanese grammar patterns and examples.", "/grammar"],
        ["Lessons", "Lessons", "Structured Japanese lessons and cultural notes.", "/lessons"],
        ["Kanji", "Kanji", "Kanji meanings, readings and writing practice.", "/blog/full-kanji-list"],
        ["Blog", "Blog article", "Guides and study resources for Japanese learners.", "/blog"],
        ["Kana keyboard", "Tool", "Type romaji to write Japanese hiragana and katakana.", "/keyboard"],
        ["Translation", "Tool", "Translate Japanese text and study translations.", "/translate"],
        ["Romaji converter", "Tool", "Convert romaji into Japanese kana.", "/romaji-converter"],
        ["Verb conjugation", "Study tool", "Japanese verb forms, groups, and conjugation practice.", "/conjugation"],
        ["Adjective conjugation", "Study tool", "Japanese i-adjective and na-adjective forms.", "/adjective-conjugation"],
        ["Kana writing", "Practice", "Practice hiragana and katakana stroke order.", "/kana-writing-practice"],
        ["Kanji writing", "Practice", "Practice kanji stroke order by JLPT level.", "/kanji-writing-practice"],
        ["Reading practice", "Practice", "Japanese reading stories and comprehension practice.", "/reading-practice"],
        ["Speaking practice", "Practice", "Japanese pronunciation and speaking practice.", "/speaking-practice"],
        ["Japanese numbers", "Tool", "Japanese number readings and counters.", "/numbers"],
        ["JLPT test sites", "Resource", "Find JLPT examination sites, cities, and countries.", "/jlpt-test-sites"],
        ["Study materials", "Resource", "Recommended Japanese textbooks and learning resources.", "/study-materials"],
        ["Learning roadmap", "Resource", "A Japanese learning roadmap from kana to JLPT.", "/learning-roadmap"],
        ["Radicals", "Reference", "Japanese kanji radicals, meanings, readings, and stroke counts.", "/radicals"],
        ["Similar kanji", "Reference", "Compare similar-looking Japanese kanji.", "/similar-kanji"],
        ["About NihongoTech", "Information", "NihongoTech Japanese learning features and tools.", "/about"],
        ["Offline study", "Information", "Download Japanese study material for offline use.", "/offline"],
    ].map(([title, type, excerpt, to]) => ({ id: `page-${to}`, title, type, excerpt, to: `${prefix}${to}`, terms: [title, type, excerpt] }));
    return { before: [...culture, ...grammar], after: [...kanji, ...pages] };
}

function lessonResults(lessons: any[], language: string, translations: Record<string, any> | undefined): SearchResult[] {
    const prefix = language === "pt" ? "/pt" : "";
    return lessons.map((lesson) => {
        const translation = translations?.[lesson.number];
        const title = translation?.title ?? lesson.title;
        const paragraphs = [...(translation?.paragraphs ?? lesson.paragraphs ?? []), ...(translation?.paragraphsAfterFigure ?? lesson.paragraphsAfterFigure ?? [])];
        return {
            id: `lesson-${lesson.number}`, title: `${lesson.number}: ${title}`, type: language === "pt" ? "Lição" : "Lesson",
            excerpt: paragraphs[0] || lesson.searchTerms, to: `${prefix}/lessons?lesson=${lesson.number.replace("Lesson ", "")}`,
            terms: [lesson.number, title, lesson.searchTerms, translation?.searchTerms, ...paragraphs],
        };
    });
}

export default function GlobalSearch({ compact = false }: { compact?: boolean }) {
    const { i18n, t } = useTranslation();
    const lp = useLocalizedPath();
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [loaded, setLoaded] = useState<Loaded>(NOTHING_LOADED);
    const inputRef = useRef<HTMLInputElement>(null);
    const language = i18n.language === "pt" ? "pt" : "en";
    const index = useMemo(() => contentIndex(language), [language]);
    const blogIndex = useRef<{ i18n: unknown; language: string; items: SearchResult[] }>();
    const text = query.trim();
    const searchable = isSearchable(text);
    const [settled, settleNow] = useSettledValue(query, SEARCH_IDLE_DELAY_MS, open);
    const searched = open && searchable && settled.trim() === text ? text : "";
    const loadedForSearch = loaded.query === searched && loaded.language === language;

    useEffect(() => {
        if (!open) return undefined;
        const timer = window.setTimeout(() => inputRef.current?.focus(), 0);
        return () => window.clearTimeout(timer);
    }, [open]);
    useEffect(() => {
        if (!open) return undefined;
        const closeOnEscape = (event: KeyboardEvent) => { if (event.key === "Escape") setOpen(false); };
        window.addEventListener("keydown", closeOnEscape);
        return () => window.removeEventListener("keydown", closeOnEscape);
    }, [open]);
    useEffect(() => {
        if (!searched) return undefined;
        let current = true;
        const translations = language === "pt" ? i18n.getResource("pt", "lessonContent", "lessons") : undefined;
        Promise.all([
            Promise.all([1, 2, 3, 4, 5].map((level) => fetchVocabularyLevel(level, language))),
            import("../LessonsPage/LessonsPage"),
            import("../../consts/blogPosts"),
            import("../../consts/blogPostsPt"),
        ]).then(([levels, lessonModule, blogModule, blogPtModule]) => {
            if (!current) return;
            const needle = normalize(searched);
            const vocabulary = levels.flat().filter((word) =>
                forms(word.word).some((value) => value.includes(needle)) || forms(word.reading).some((value) => value.includes(needle)) ||
                wordMatchesRomaji(word, needle) || [...(word.meanings || []), ...(word.englishMeanings || [])].some((meaning) => forms(meaning).some((value) => value.includes(needle)))
            ).slice(0, 30).map((word) => ({ id: `vocabulary-${word.id}`, title: `${word.word} (${word.reading})`, type: language === "pt" ? "Vocabulário" : "Vocabulary", excerpt: (word.meanings || []).join(", "), to: lp("/vocabulary"), terms: [] }));
            const lessons = lessonResults(lessonModule.lessons, language, translations).filter((lesson) => matches(lesson, searched));
            if (blogIndex.current?.i18n !== i18n || blogIndex.current.language !== language) {
                const posts = blogModule.blogPosts as unknown as BlogPostRecord[];
                blogIndex.current = { i18n, language, items: blogResults(posts, blogPtModule.blogPostContentPt as Record<string, unknown>, language, i18n) };
            }
            const blogs = blogIndex.current.items.filter((item) => matches(item, searched));
            setLoaded({ query: searched, language, vocabulary, lessons, blogs });
        }).catch(() => { if (current) setLoaded({ ...NOTHING_LOADED, query: searched, language }); });
        return () => { current = false; };
    }, [i18n, language, lp, searched]);

    const loading = Boolean(searched) && !loadedForSearch;
    const contentMatches = useMemo(() => ({
        before: searched ? index.before.filter((item) => matches(item, searched)) : [],
        after: searched ? index.after.filter((item) => matches(item, searched)) : [],
    }), [index, searched]);
    const found = loadedForSearch ? loaded : NOTHING_LOADED;
    const results = searched ? [...found.lessons, ...contentMatches.before, ...found.blogs, ...contentMatches.after, ...found.vocabulary].slice(0, 40) : [];

    let status = "";
    if (loading) status = t("globalSearch.searching");
    else if (searchable && !searched) status = t("globalSearch.waiting");
    else if (text && !searchable) status = t("globalSearch.tooShort");
    else if (searched && !results.length) status = t("globalSearch.noResults");
    else if (searched) status = t("globalSearch.resultCount", { count: results.length });

    function change(event: ChangeEvent<HTMLInputElement>) {
        const next = event.target.value;
        setQuery(next);
        if (!isSearchable(next)) settleNow(next);
    }
    function submit(event: FormEvent) {
        event.preventDefault();
        if (searchable) settleNow(query);
    }
    const label = language === "pt" ? "Pesquisar em NihongoTech" : "Search NihongoTech";
    return <>
        <button type="button" className={compact ? styles.compactTrigger : styles.trigger} onClick={() => setOpen(true)} aria-label={label}>
            <FontAwesomeIcon icon={faMagnifyingGlass} />{!compact && <span>{language === "pt" ? "Pesquisar" : "Search"}</span>}
        </button>
        {open && <div className={styles.backdrop} role="presentation" onMouseDown={() => setOpen(false)}>
            <section className={styles.dialog} role="dialog" aria-modal="true" aria-label={label} onMouseDown={(event) => event.stopPropagation()}>
                <form onSubmit={submit}><label htmlFor="global-search" className={styles.label}>{label}</label><input id="global-search" ref={inputRef} value={query} onChange={change} aria-describedby="global-search-status" placeholder={language === "pt" ? "Palavra, romaji ou frase…" : "Word, romaji, or phrase…"} /></form>
                <div id="global-search-status" role="status" aria-live="polite">{status && <p className={styles.status}>{status}</p>}</div>
                <div className={styles.results}>{results.map((result) => <Link key={result.id} to={result.to} onClick={() => setOpen(false)} className={styles.result}><span className={styles.type}>{result.type}</span><strong>{result.title}</strong><span>{result.excerpt}</span></Link>)}</div>
                <button type="button" className={styles.close} onClick={() => setOpen(false)}>{language === "pt" ? "Fechar" : "Close"}</button>
            </section>
        </div>}
    </>;
}
